import { RoomStateRubricResult } from '@breakoutlearning/firebase-repository/models/RoomStateRubricResult'
import { SlideDeckRubricTestResult } from '@breakoutlearning/firebase-repository/models/SlideDeckRubricTestResult'
import type { SlideRubric } from '@breakoutlearning/firebase-repository/models/SlideRubric'
import { RubricType } from '@breakoutlearning/firebase-repository/types'
import classNames from 'classnames'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { CheckIcon } from 'components/icons/Check'
import { Close } from 'components/icons/Close'
import { useRubricTypes } from 'hooks/useRubricTypes'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'

export const RubricScoreWidgetBox = observer(function RubricScoreWidgetBox({
  rubricType,
  rubricText,
  rubricResult,
  scoreLabel,
}: {
  rubricType?: RubricType
  rubricText: string
  rubricResult: RoomStateRubricResult | SlideDeckRubricTestResult | number
  scoreLabel?: string
}) {
  const rubricTypeObj = useRubricTypes().getRubricByType(rubricType)
  const { tt } = useTranslationTyped()

  const score = useMemo(() => {
    if (
      rubricResult instanceof SlideDeckRubricTestResult ||
      rubricResult instanceof RoomStateRubricResult
    )
      return rubricResult.data.score
    return rubricResult
  }, [rubricResult])

  const rubricColor = rubricTypeObj.getScoreBgColor(score)
  const rubricColorWithOpacity = rubricTypeObj.getScoreBgColor(score, {
    opacity: 0.3,
  })
  const rubricTextColor = rubricTypeObj.getScoreTextColor(score)

  // if we have a text color defined, don't manipulate the bg color opacity
  // else we use the bg color with opacity for the bg, and the solid color for the text
  const bgColor = rubricTextColor ? rubricColor : rubricColorWithOpacity
  const fgColor = rubricTextColor ?? rubricColor

  // The box is a bare number or icon and the rubric name is only in the
  // tooltip. Label the box itself so screen readers and ZoomText's mouse echo
  // (which skips sr-only text) both read the full meaning.
  const spokenScore =
    rubricType === RubricType.passFail
      ? scoreLabel || (score === 1 ? tt.a11y.pass() : tt.a11y.fail())
      : scoreLabel
        ? `${score}, ${scoreLabel}`
        : String(score)
  const ariaLabel = `${rubricText}: ${spokenScore}`

  let boxElement: JSX.Element
  // we do custom check/cross for pass/fail rubric, rest we just show the score as integer
  if (rubricType === RubricType.passFail) {
    boxElement = (
      <BreakoutTooltip content={rubricText} placement="top">
        <div className="group relative">
          <div
            role="img"
            aria-label={ariaLabel}
            className="flex h-6 w-6 items-center justify-center rounded-sm"
            style={{ backgroundColor: rubricColorWithOpacity }}
          >
            {/*  if score is 1 show CheckIcon otherwise show CloseIcon */}
            {score === 1 ? (
              <CheckIcon
                className="w-4 text-shadow-white"
                style={{
                  color: rubricColor,
                }}
              />
            ) : (
              <Close
                className="w-4 text-breakout-red text-shadow-white"
                style={{
                  color: rubricColor,
                }}
              />
            )}
          </div>
        </div>
      </BreakoutTooltip>
    )
  } else {
    boxElement = (
      <BreakoutTooltip content={rubricText} placement="top">
        <div className="group relative">
          <div
            role="img"
            aria-label={ariaLabel}
            className="flex h-6 w-6 items-center justify-center rounded-sm"
            style={{
              backgroundColor: bgColor,
            }}
          >
            <strong
              className={'text-label-small'}
              style={{
                color: fgColor,
              }}
            >
              {score}
            </strong>
          </div>
        </div>
      </BreakoutTooltip>
    )
  }

  return boxElement
})

export const RubricScoreWidget = observer(function RubricScoreWidget({
  rubric,
  rubricResult,
  showTitle = false,
}: {
  rubric: SlideRubric
  rubricResult: RoomStateRubricResult | SlideDeckRubricTestResult | number
  showTitle?: boolean
}) {
  const rubricType = useRubricTypes().getRubricByType(rubric.rubricType)

  const score = useMemo(() => {
    if (
      rubricResult instanceof SlideDeckRubricTestResult ||
      rubricResult instanceof RoomStateRubricResult
    )
      return rubricResult.data.score
    return rubricResult
  }, [rubricResult])

  const labelText = rubricType.getScoreLabel(score, rubric)

  const boxElement = (
    <RubricScoreWidgetBox
      rubricType={rubric.rubricType}
      rubricText={rubric.data.rubric}
      rubricResult={rubricResult}
      scoreLabel={labelText}
    />
  )

  if (!showTitle) return boxElement

  return (
    <div className="flex flex-row items-center gap-1 overflow-hidden">
      {boxElement}
      <strong
        className={classNames('truncate text-label-small text-nowrap')}
        //style={{ color: rubricColor }}
      >
        {labelText}
      </strong>
    </div>
  )
})

/**
 * pass the rubric list sorted
 */
export const RubricScoreList = ({
  rubricList,
  rubricResults,
  className,
  onClick,
  wasAloneInExperience,
  userRole,
  roleUnknown = false,
}: {
  rubricList: SlideRubric[]
  // `number[]` carries plain scores from the gradebook API: one score, or an
  // empty array when the rubric's data is missing.
  rubricResults: Map<
    SlideRubric,
    RoomStateRubricResult[] | RoomStateRubricResult | number[]
  >
  onClick?: () => void
  className?: string
  wasAloneInExperience?: boolean
  userRole: string | null
  // The caller has no room data to read the user's role from (the Gradebook
  // API rows for students the cubit hasn't loaded). A score that is there is
  // then shown; only a rubric without one falls back to "not generated".
  roleUnknown?: boolean
}) => {
  const types = useRubricTypes()
  const { tt } = useTranslationTyped()

  const items = rubricList.map((rubric, i) => {
    const result = rubricResults.get(rubric)
    const rubricTypeObj = types.getRubricByType(rubric.rubricType)

    const isMissingData = !result || (Array.isArray(result) && !result.length)

    const rubricRoles = rubric.data.assignmentRoles ?? []
    const isRoleLimited = rubricRoles.length > 0
    const userMissingAppropriateRole =
      isRoleLimited &&
      (roleUnknown
        ? isMissingData
        : !userRole || !rubricRoles.includes(userRole))

    if (
      (isMissingData &&
        rubricTypeObj.excludeSoloExperiences &&
        wasAloneInExperience) ||
      userMissingAppropriateRole
    ) {
      return {
        key: rubric.id + i,
        label: `${rubric.data.rubric}: ${tt.rubric.rubric_not_generated()}`,
        node: (
          <ExcludedSoloExperienceOrRoleLimitedBox
            key={rubric.id + i}
            rubricText={rubric.data.rubric}
          />
        ),
      }
    }

    if (isMissingData) {
      return {
        key: rubric.id + i,
        label: `${rubric.data.rubric}: ${tt.rubric.rubric_data_missing()}`,
        node: (
          <DataMissingBox key={rubric.id + i} rubricText={rubric.data.rubric} />
        ),
      }
    }

    const resultDeconstructed = Array.isArray(result) ? result[0] : result
    const score =
      resultDeconstructed instanceof RoomStateRubricResult
        ? resultDeconstructed.data.score
        : resultDeconstructed
    const scoreLabel =
      rubric.rubricType === RubricType.passFail
        ? rubricTypeObj.getScoreLabel(score, rubric) || String(score)
        : String(score)

    return {
      key: rubric.id + i,
      label: `${rubric.data.rubric}: ${scoreLabel}`,
      node: (
        <span key={rubric.id + i} data-testid="rubric-score-widget">
          <RubricScoreWidget
            rubric={rubric}
            rubricResult={resultDeconstructed}
          />
        </span>
      ),
    }
  })

  const sharedClassName = classNames(
    'flex w-min flex-row items-center gap-1 rounded-lg border border-transparent p-1',
    className
  )

  if (!onClick) {
    return (
      <div className={sharedClassName}>{items.map((item) => item.node)}</div>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="rubric-score-list"
      aria-haspopup="dialog"
      aria-label={[
        tt.instructor_assignment.rubric_assessment(),
        items.map((item) => item.label).join(', '),
      ].join(': ')}
      className={classNames(
        sharedClassName,
        'focus-ring-floating cursor-pointer hover:border hover:border-outline-light hover:bg-surface-dim'
      )}
    >
      {items.map((item) => item.node)}
    </button>
  )
}

export function ExcludedSoloExperienceOrRoleLimitedBox({
  rubricText,
}: {
  rubricText?: string
}) {
  const { tt } = useTranslationTyped()
  const status = tt.rubric.rubric_not_generated()
  return (
    <BreakoutTooltip content={status} placement="top">
      <div
        role="img"
        aria-label={rubricText ? `${rubricText}: ${status}` : status}
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-surface-dim text-center text-label-medium text-on-surface-var"
        data-testid="rubric-role-limited-box"
      >
        -
      </div>
    </BreakoutTooltip>
  )
}

export function DataMissingBox({ rubricText }: { rubricText?: string }) {
  const { tt } = useTranslationTyped()
  const status = tt.rubric.rubric_data_missing()
  return (
    <BreakoutTooltip content={status} placement="top">
      <div
        role="img"
        aria-label={rubricText ? `${rubricText}: ${status}` : status}
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-on-surface-disabled text-center text-label-medium text-on-surface"
        data-testid="rubric-data-missing-box"
      >
        ?
      </div>
    </BreakoutTooltip>
  )
}
